import React from 'react';
import Header, { BreadcrumbItem } from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { usePageTracking } from '../hooks/usePageTracking';
import styles from '../styles/discoveryPage.module.css';

interface DiscoveryPageProps {
  user?: any;
  onLogout: () => void;
  onLogin: () => void;
}

const DiscoveryPage: React.FC<DiscoveryPageProps> = ({ user, onLogout, onLogin }) => {
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'Discover', path: '/discover', active: true }
  ];

  // Track page visit
  usePageTracking('Discover Recovery Programs');

  return (
    <div className={styles.discoveryPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Recovery Programs</h1>
          <p className={styles.pageDescription}>
            Explore 12-step recovery programs. Each offers a proven path to healing and support for those struggling with addiction.
          </p>
        </div>

        {/* Programs Grid Section */}
        <section className={styles.programsSection}>
          <div className={styles.programsGrid}>
            <div className={styles.programCard}>
              <h3>SAA</h3>
              <h4 className={styles.programFullName}>Sex Addicts Anonymous</h4>
              <p>A 12-step fellowship for recovery from sexual addiction</p>
              <a href="/saa" className={styles.programButton}>Learn about Program</a>
            </div>
            
            <div className={styles.programCard}>
              <h3>AA</h3>
              <h4 className={styles.programFullName}>Alcoholics Anonymous</h4>
              <p>A 12-step fellowship for recovery from alcoholism</p>
              <a href="/aa" className={styles.programButton}>Learn about Program</a>
            </div>
            
            <div className={styles.programCard}>
              <h3>NA</h3>
              <h4 className={styles.programFullName}>Narcotics Anonymous</h4>
              <p>A 12-step fellowship for recovery from drug addiction</p>
              <a href="/na" className={styles.programButton}>Learn about Program</a>
            </div>
            
            <div className={styles.programCard}>
              <h3>OA</h3>
              <h4 className={styles.programFullName}>Overeaters Anonymous</h4>
              <p>A 12-step fellowship for recovery from food addiction</p>
              <a href="/oa" className={styles.programButton}>Learn about Program</a>
            </div>
            
            <div className={styles.programCard}>
              <h3>GA</h3>
              <h4 className={styles.programFullName}>Gamblers Anonymous</h4>
              <p>A 12-step fellowship for recovery from gambling addiction</p>
              <a href="/ga" className={styles.programButton}>Learn about Program</a>
            </div>
            
            <div className={styles.programCard}>
              <h3>DA</h3>
              <h4 className={styles.programFullName}>Debtors Anonymous</h4>
              <p>A 12-step fellowship for recovery from compulsive debt</p>
              <a href="/da" className={styles.programButton}>Learn about Program</a>
            </div>
            
            <div className={styles.programCard}>
              <h3>CoDA</h3>
              <h4 className={styles.programFullName}>Co-Dependents Anonymous</h4>
              <p>A 12-step fellowship for recovery from codependency</p>
              <a href="/coda" className={styles.programButton}>Learn about Program</a>
            </div>
            
            <div className={styles.programCard}>
              <h3>CA</h3>
              <h4 className={styles.programFullName}>Cocaine Anonymous</h4>
              <p>A 12-step fellowship for recovery from cocaine addiction</p>
              <a href="/ca" className={styles.programButton}>Learn about Program</a>
            </div>
          </div>
        </section>

        {/* Getting Started Section */}
        <section className={styles.gettingStartedSection}>
          <h2>Getting Started</h2>
          <div className={styles.gettingStartedContent}>
            <div className={styles.gettingStartedCard}>
              <h3>Not Sure Where to Start?</h3>
              <p>
                If you're unsure which program might be right for you, start with our self-assessment tool. 
                It can help identify patterns and suggest programs that might be a good fit.
              </p>
              <a href="/self-assess" className={styles.primaryButton}>Take Self-Assessment</a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default DiscoveryPage;
